// Loader for Google Identity Services (GIS) — the `accounts.google.com/gsi/client`
// script that renders the "Sign in with Google" button and hands back a signed
// ID token (JWT) via the `callback`. That credential is what `auth/google` on
// dabble-rest verifies server-side.
//
// The npm ecosystem's type packages for GIS lag the API and pull in globals we
// don't want, so the minimal surface we use is typed locally instead.

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const SCRIPT_ID = 'google-gsi-client';

/** The response GIS passes to `initialize`'s callback after a successful sign-in. */
export interface GoogleCredentialResponse {
  /** The signed Google ID token (JWT). POST it to `auth/google` for verification. */
  credential: string;
  /** How the credential was selected, e.g. 'btn', 'user', 'auto'. */
  select_by?: string;
  clientId?: string;
}

export interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  context?: 'signin' | 'signup' | 'use';
  ux_mode?: 'popup' | 'redirect';
  use_fedcm_for_prompt?: boolean;
}

export interface GsiButtonConfiguration {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  /** Button width in px (max 400 per GIS docs). */
  width?: number;
  locale?: string;
}

/** The `window.google.accounts.id` surface (only the members this app uses). */
export interface GoogleAccountsId {
  initialize(config: GoogleIdConfiguration): void;
  renderButton(parent: HTMLElement, options?: GsiButtonConfiguration): void;
  prompt(): void;
  cancel(): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

let loadPromise: Promise<GoogleAccountsId> | null = null;

/**
 * Load Google Identity Services once and resolve `window.google.accounts.id`.
 *
 * Idempotent: concurrent and repeat callers share one cached promise and one
 * injected `<script>` tag. A failed load (offline, blocked by an extension)
 * rejects — and clears the cache, so a later attempt re-injects the script
 * instead of being stuck with a permanently rejected promise.
 */
export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  if (!loadPromise) {
    loadPromise = inject();
    loadPromise.catch(() => {
      loadPromise = null;
    });
  }
  return loadPromise;
}

function inject(): Promise<GoogleAccountsId> {
  return new Promise<GoogleAccountsId>((resolve, reject) => {
    // Already available (previous load, or another consumer injected it).
    const existing = window.google?.accounts?.id;
    if (existing) {
      resolve(existing);
      return;
    }

    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    const el = script;

    el.addEventListener(
      'load',
      () => {
        const api = window.google?.accounts?.id;
        if (api) {
          resolve(api);
        } else {
          // Remove the neutered tag too (extension stubbed the script body):
          // a retry must inject a fresh script, not attach a `load` listener
          // to a tag whose load event already fired — that would never settle.
          el.remove();
          reject(new Error('Google Identity Services script loaded but window.google.accounts.id is missing'));
        }
      },
      { once: true },
    );
    el.addEventListener(
      'error',
      () => {
        // Remove the dead tag so the next attempt injects a fresh one.
        el.remove();
        reject(new Error('Failed to load Google Identity Services'));
      },
      { once: true },
    );
  });
}
